import type {
    DyCustomJsonResponseType,
    SelectorsType,
    SortedDyDataType,
    RawDyDataType,
    SortedDyRecommendationsType,
} from 'types/dy';

const initialSelectors: SelectorsType = {
    welcomePopup: '',
    exitPopup: '',
    triggerPopup: '',
};

/**
 * Assigns a key to a selector in order
 * @param {string[]} selectors DY selectors
 * @return {SelectorsType} selector keys
 */
export const setDySelectors = (selectors: string[]): SelectorsType =>
    Object.keys(initialSelectors).reduce(
        (acc, key, index) => {
            return {
                ...acc,
                [key]: selectors[index] || '',
            };
        },
        {
            ...initialSelectors,
        },
    );

/**
 * Get sorted and separate template data and analytics for storage
 * @param {RawDyDataType} data with variations from response
 * @return {SortedDyDataType} sorting data
 */
export const getSortedDyData = (data: RawDyDataType): SortedDyDataType => {
    const keys = Object.keys(data);
    return keys.reduce(
        (acc, key) => {
            return {
                value: {
                    ...acc.value,
                    [key]: (data[key] && data[key]?.payload?.data?.value) || {},
                },
                analytics: {
                    ...acc.analytics,
                    [key]: (data[key] && data[key]?.payload?.data?.analytics) || {},
                },
            };
        },
        {
            value: {},
            analytics: {},
        },
    );
};

/**
 * Get sorted and separate recommendations data for storage
 * @param {RawDyDataType} data with variations from response
 * @return {SortedDyRecommendationsType} sorting data
 */
export const getSortedRecommendationsData = (data: RawDyDataType): SortedDyRecommendationsType => {
    const keys = Object.keys(data);
    return keys.reduce(
        (acc, key) => {
            return {
                value: {
                    ...acc.value,
                    [key]: (data[key] && data[key]?.payload?.data?.slots) || {},
                },
            };
        },
        {
            value: {},
        },
    );
};

/**
 * Get draft sorted data {key: value} from Dy
 * @param {DyCustomJsonResponseType} data - response from dy
 * @returns {RawDyDataType} sorted data
 */
export const getDyDataBySelector = (data: DyCustomJsonResponseType): RawDyDataType => {
    return data?.choices.reduce((acc, item) => {
        if (item.name && item.variations[0]) {
            acc[item.name] = item.variations[0];
        }
        return acc;
    }, {} as RawDyDataType);
};
